import React, { useState, useEffect, useRef } from "react";
import {
  Container,
  Button,
  TextField,
  Select,
  MenuItem,
  Typography,
  Box,
  Card,
  CardContent,
  Tabs,
  Tab,
  CircularProgress,
  Autocomplete,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack
} from "@mui/material";
import { useMsal } from "@azure/msal-react";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import ClearIcon from "@mui/icons-material/Clear";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CodeIcon from "@mui/icons-material/Code";
import HttpIcon from "@mui/icons-material/Http";
import SecurityIcon from "@mui/icons-material/Security";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SendIcon from "@mui/icons-material/Send";
import DoneIcon from "@mui/icons-material/Done";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { 
  Grid,
  Divider, 
  Paper, 
  IconButton, 
  Chip, 
  FormControl, 
  InputLabel, 
  Tooltip, 
  Alert 
} from "@mui/material";
import GraphApiTemplatesComponent from "./extensions/GraphApiTemplatesComponent";
import { HttpMethod } from "./extensions/GraphApiTemplates"; // Import the HttpMethod type

// Normalize user-entered Graph path by removing full domain and version prefixes
function normalizeQuery(input: string): string {
  if (!input) return '';
  let q = input.trim();
  q = q.replace(/^https?:\/\/graph\.microsoft\.com\//i, '/');
  if (!q.startsWith('/')) q = '/' + q;
  q = q.replace(/^\/(v1\.0|beta)(?=\/|$)/i, '');
  if (!q.startsWith('/')) q = '/' + q;
  return q;
}

/**
 * Common Graph API endpoints for autocomplete suggestions
 */
const COMMON_ENDPOINTS = [
  // User & Profile
  { value: '/me', description: 'Get the signed-in user profile', category: 'User & Profile' },
  { value: '/me/messages', description: 'Get user emails', category: 'User & Profile' },
  { value: '/me/calendar', description: 'Get user calendar', category: 'User & Profile' },
  { value: '/me/events', description: 'Get user calendar events', category: 'User & Profile' },
  { value: '/me/drive', description: 'Get user OneDrive', category: 'User & Profile' },
  { value: '/me/photo', description: 'Get user profile photo', category: 'User & Profile' },
  { value: '/me/manager', description: 'Get user manager', category: 'User & Profile' },
  { value: '/me/directReports', description: 'Get direct reports', category: 'User & Profile' },
  { value: '/me/memberOf', description: 'Get user group memberships', category: 'User & Profile' },
  
  // Users (Admin)
  { value: '/users', description: 'List all users', category: 'Users (Admin)' },
  { value: '/users/{id}', description: 'Get a specific user by ID', category: 'Users (Admin)' },
  { value: '/users?$filter=startswith(displayName,\'\')&$select=displayName,id', description: 'Filter users by display name', category: 'Users (Admin)' },
  { value: '/users?$count=true', description: 'Get user count (requires ConsistencyLevel header)', category: 'Users (Admin)' },
  
  // Groups
  { value: '/groups', description: 'List all groups', category: 'Groups' },
  { value: '/groups/{id}', description: 'Get a specific group by ID', category: 'Groups' },
  { value: '/groups/{id}/members', description: 'Get group members', category: 'Groups' },
  { value: '/groups/{id}/owners', description: 'Get group owners', category: 'Groups' },
  
  // Applications & Service Principals
  { value: '/applications', description: 'List applications', category: 'Applications & Service Principals' },
  { value: '/applications/{id}', description: 'Get a specific application by ID', category: 'Applications & Service Principals' },
  { value: '/servicePrincipals', description: 'List service principals', category: 'Applications & Service Principals' },
  { value: '/servicePrincipals/{id}', description: 'Get a specific service principal by ID', category: 'Applications & Service Principals' },
  { value: '/servicePrincipals/{id}/appRoleAssignments', description: 'List app role assignments for a service principal', category: 'Applications & Service Principals' },
  { value: '/servicePrincipals/{id}/appRoleAssignedTo', description: 'List principals assigned to this app', category: 'Applications & Service Principals' },
  { value: '/oauth2PermissionGrants', description: 'List OAuth2 permission grants (delegated permissions)', category: 'Applications & Service Principals' },
  { value: '/oauth2PermissionGrants/{id}', description: 'Get a specific OAuth2 permission grant', category: 'Applications & Service Principals' },
  { value: '/servicePrincipals/{id}/oauth2PermissionGrants', description: 'List OAuth2 grants for a service principal', category: 'Applications & Service Principals' },
  
  // Directory & Organization
  { value: '/organization', description: 'Get tenant organization details', category: 'Directory & Organization' },
  { value: '/directory/deletedItems/microsoft.graph.application', description: 'List deleted applications', category: 'Directory & Organization' },
  { value: '/domains', description: 'List verified domains', category: 'Directory & Organization' },
  { value: '/subscribedSkus', description: 'List subscribed SKUs/licenses', category: 'Directory & Organization' },
  
  // Identity & Security
  { value: '/identity/conditionalAccess/policies', description: 'List conditional access policies', category: 'Identity & Security' },
  { value: '/identity/conditionalAccess/namedLocations', description: 'List named locations', category: 'Identity & Security' },
  { value: '/identityProtection/riskyUsers', description: 'List risky users', category: 'Identity & Security' },
  { value: '/identityProtection/riskDetections', description: 'List risk detections', category: 'Identity & Security' },
  
  // Reports & Audit Logs
  { value: '/auditLogs/directoryAudits', description: 'Get directory audit logs', category: 'Reports & Audit Logs' },
  { value: '/auditLogs/signIns', description: 'Get sign-in logs', category: 'Reports & Audit Logs' },
  { value: '/reports/getEmailActivityUserDetail(period=\'D7\')', description: 'Get email activity report (7 days)', category: 'Reports & Audit Logs' },
  
  // Teams & Chats
  { value: '/me/chats', description: 'Get user chats', category: 'Teams & Chats' },
  { value: '/teams/{id}', description: 'Get a specific team', category: 'Teams & Chats' },
  { value: '/teams/{id}/channels', description: 'List team channels', category: 'Teams & Chats' },
  
  // Sites & SharePoint
  { value: '/sites/{site-id}', description: 'Get a SharePoint site', category: 'Sites & SharePoint' },
  { value: '/sites/root', description: 'Get root SharePoint site', category: 'Sites & SharePoint' },
  { value: '/sites/{site-id}/lists', description: 'Get site lists', category: 'Sites & SharePoint' }
];

interface GraphExplorerProps {
  accessToken: string;  // initial token from parent
  onBack: () => void;
}

/**
 * A small dictionary of Graph endpoints => required delegated scopes,
 * potentially also distinguishing between read vs. write (by method).
 */
const endpointScopeMap: Array<{
  match: RegExp;               // regex to see if query matches (e.g. /users, /me/calendar)
  readScopes: string[];        // scopes for read-only operations
  writeScopes?: string[];      // scopes for write operations
  requiresAdmin?: boolean;     // if the endpoint definitely requires admin privileges
}> = [
  // GET /me or /me/...
  {
    match: /^\/me/i,
    readScopes: ["User.Read"],
  },
  // /users ...
  {
    match: /^\/users/i,
    readScopes: ["User.Read.All"],
    writeScopes: ["User.ReadWrite.All"],
    requiresAdmin: true // Usually "User.ReadWrite.All" requires Admin
  },
  // /groups ...
  {
    match: /^\/groups/i,
    readScopes: ["Group.Read.All"],
    writeScopes: ["Group.ReadWrite.All"],
    requiresAdmin: true
  },
  // /applications ...
  {
    match: /^\/applications/i,
    readScopes: ["Application.ReadWrite.All"],
    // For “read,” you actually need .ReadWrite or .Read.OwnedBy etc. It's complicated
    writeScopes: ["Application.ReadWrite.OwnedBy", "Application.ReadWrite.All"],
    requiresAdmin: true
  },
  // /organization ...
  {
    match: /^\/organization/i,
    readScopes: ["Organization.Read.All"],
    requiresAdmin: true
  },
  // /auditLogs ...
  {
    match: /^\/auditLogs/i,
    readScopes: ["AuditLog.Read.All"],
    requiresAdmin: true
  },
  // /messages ...
  {
    match: /^\/messages/i,
    readScopes: ["Mail.Read.All"],
    requiresAdmin: false
  },  {
    match: /^\/me\/messages/i,
    readScopes: ["Mail.Read"],
    requiresAdmin: false
  },
  
  // Identity Protection
  {
    match: /^\/identityProtection\/riskDetections/i,
    readScopes: ["IdentityRiskyUser.Read.All"],
    requiresAdmin: true
  },
  {
    match: /^\/identityProtection\/riskyUsers/i,
    readScopes: ["IdentityRiskyUser.Read.All"],
    writeScopes: ["IdentityRiskyUser.ReadWrite.All"],
    requiresAdmin: true
  },
  
  // Conditional Access
  {
    match: /^\/identity\/conditionalAccess\/policies/i,
    readScopes: ["Policy.Read.All"],
    writeScopes: ["Policy.ReadWrite.ConditionalAccess"],
    requiresAdmin: true
  },
  {
    match: /^\/identity\/conditionalAccess\/namedLocations/i,
    readScopes: ["Policy.Read.All"],
    writeScopes: ["Policy.ReadWrite.ConditionalAccess"],
    requiresAdmin: true
  },
  {
    match: /^\/identity\/conditionalAccess\/authenticationStrengths/i,
    readScopes: ["Policy.Read.All"],
    writeScopes: ["Policy.ReadWrite.ConditionalAccess"],
    requiresAdmin: true
  },
  
  // Authentication Methods
  {
    match: /^\/users\/.*\/authentication\/methods/i,
    readScopes: ["UserAuthenticationMethod.Read.All"],
    writeScopes: ["UserAuthenticationMethod.ReadWrite.All"],
    requiresAdmin: true
  },
  {
    match: /^\/reports\/authenticationMethods/i,
    readScopes: ["Reports.Read.All"],
    requiresAdmin: true
  },
  {
    match: /^\/policies\/authenticationMethodsPolicy/i,
    readScopes: ["Policy.Read.All"],
    writeScopes: ["Policy.ReadWrite.AuthenticationMethod"],
    requiresAdmin: true
  },
  
  // Directory Settings
  {
    match: /^\/settings/i,
    readScopes: ["Directory.Read.All"],
    writeScopes: ["Directory.ReadWrite.All"],
    requiresAdmin: true
  },
  
  // Service Principals
  {
    match: /^\/servicePrincipals/i,
    readScopes: ["Application.Read.All"],
    writeScopes: ["Application.ReadWrite.All"],
    requiresAdmin: true
  },
  
  // OAuth2 Permission Grants
  {
    match: /^\/oauth2PermissionGrants/i,
    readScopes: ["Directory.Read.All"],
    writeScopes: ["Directory.ReadWrite.All"],
    requiresAdmin: true
  },
  
  // App Role Assignments
  {
    match: /^\/servicePrincipals\/.*\/appRoleAssignments/i,
    readScopes: ["Application.Read.All"],
    writeScopes: ["AppRoleAssignment.ReadWrite.All"],
    requiresAdmin: true
  },
  {
    match: /^\/servicePrincipals\/.*\/appRoleAssignedTo/i,
    readScopes: ["Application.Read.All"],
    writeScopes: ["AppRoleAssignment.ReadWrite.All"],
    requiresAdmin: true
  }

  // If you expand for /drive, /sites, etc., add them below ...
];

/** Helper to decode a JWT for scope inspection. */
function decodeJwt(token: string): any {
  try {
    const parts = token.split(".");
    if (parts.length < 2) return null;
    const base64Url = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64Url)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    console.error("Failed to decode token:", e);
    return null;
  }
}

/**
 * Given a Graph endpoint + method, attempt to identify which delegated scopes are required.
 * For advanced usage, you'd merge in user-specified scopes or handle partial overlaps.
 */
function detectNeededScopes(query: string, method: HttpMethod): {
  possibleScopes: string[];
  definitelyRequiresAdmin: boolean;
} {
  const lower = query.toLowerCase().trim();
  const matchedItem = endpointScopeMap.find((item) => item.match.test(lower));
  if (!matchedItem) {
    // Smarter fallback based on URL patterns
    if (lower.includes('/me/')) {
      // If it contains /me/ but we don't have a specific mapping, it's likely
      // a personal endpoint that needs at least User.Read
      return {
        possibleScopes: ["User.Read"],
        definitelyRequiresAdmin: false
      };
    } else if (lower.includes('/identity/') || lower.includes('protection')) {
      // Identity-related endpoints often need admin access
      return {
        possibleScopes: ["Directory.Read.All", "Policy.Read.All"],
        definitelyRequiresAdmin: true
      };
    } else if (lower.includes('/policies/') || lower.includes('/policy/')) {
      // Policy-related endpoints
      return {
        possibleScopes: ["Policy.Read.All"],
        definitelyRequiresAdmin: true
      };
    } else if (lower.includes('/directory')) {
      // Directory-related endpoints
      return {
        possibleScopes: ["Directory.Read.All"],
        definitelyRequiresAdmin: true
      };
    } else {
      // Generic fallback - warn that we couldn't detect specific scopes
      console.warn(`Could not detect specific scopes for endpoint: ${query}`);
      return {
        possibleScopes: ["User.Read", "Directory.Read.All"],
        definitelyRequiresAdmin: method !== "GET" // Assume write operations need admin
      };
    }
  }

  // If method is GET => use readScopes
  if (method === "GET") {
    return {
      possibleScopes: matchedItem.readScopes,
      definitelyRequiresAdmin: matchedItem.requiresAdmin ?? false
    };
  } else {
    // For POST/PATCH/DELETE => we attempt writeScopes if available, else fallback to read
    return {
      possibleScopes: matchedItem.writeScopes ?? matchedItem.readScopes,
      definitelyRequiresAdmin: matchedItem.requiresAdmin ?? false
    };
  }
}

export const GraphExplorer: React.FC<GraphExplorerProps> = ({ accessToken, onBack }) => {
  // ========================
  // 1) MSAL & State
  // ========================
  const { instance, accounts, inProgress } = useMsal();
  // inProgress can be 'login', 'acquireToken', 'handleRedirect', or 'none'

  // CORE states from your original code:
  const [query, setQuery] = useState<string>("/me");
  const [method, setMethod] = useState<HttpMethod>("GET");
  const [graphVersion, setGraphVersion] = useState<'v1.0' | 'beta'>('v1.0');
  const [body, setBody] = useState("");
  const [customHeaders, setCustomHeaders] = useState("");
  const [response, setResponse] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [tabIndex, setTabIndex] = useState<number>(0);

  // For dynamic scopes/consent:
  const [currentAccessToken, setCurrentAccessToken] = useState<string>(accessToken);
  const [neededScopes, setNeededScopes] = useState<string[]>([]);
  const [missingScopes, setMissingScopes] = useState<string[]>([]);
  const [requiresAdmin, setRequiresAdmin] = useState<boolean>(false);
  // We'll also track a small "isRequestingConsent" to prevent double popups.
  const [isRequestingConsent, setIsRequestingConsent] = useState<boolean>(false);

  const [copySuccess, setCopySuccess] = useState<boolean>(false);
  const [aboutExpanded, setAboutExpanded] = useState<boolean>(false);
    /**
   * Handler for applying a selected template from the GraphApiTemplatesComponent
   */
  const handleApplyTemplate = (
    templateMethod: HttpMethod,
    templateEndpoint: string,
    templateBody: string,
    templateHeaders: string
  ) => {
    setMethod(templateMethod);
    setQuery(templateEndpoint);
    
    // Set the body if not GET/DELETE
    if (templateMethod !== "GET" && templateMethod !== "DELETE") {
      setBody(templateBody);
    }
    
    // Set headers if provided
    try {
      const parsedHeaders = JSON.parse(templateHeaders);
      if (Object.keys(parsedHeaders).length > 0) {
        setCustomHeaders(templateHeaders);
      }
    } catch (e) {
      console.error("Error parsing template headers:", e);
    }
    
    // Reset response and errors
    setResponse("");
    setErrorMessage("");
    
    // Update the scopes based on the new endpoint
  const { possibleScopes, definitelyRequiresAdmin } = detectNeededScopes(normalizeQuery(templateEndpoint), templateMethod);
    setNeededScopes(possibleScopes);
    setRequiresAdmin(definitelyRequiresAdmin);
    
    // The missing scopes will be recalculated by the useEffect that depends on neededScopes
  };

  // Add this effect to handle changes to the accessToken prop
  useEffect(() => {
    if (accessToken && accessToken !== currentAccessToken) {
      setCurrentAccessToken(accessToken);
    }
  }, [accessToken, currentAccessToken]);

  // Add this helper function as well
  const handleCopyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  // (A) Recompute “needed scopes” each time query/method changes
  useEffect(() => {
  const { possibleScopes, definitelyRequiresAdmin } = detectNeededScopes(normalizeQuery(query), method);
    setNeededScopes(possibleScopes);
    setRequiresAdmin(definitelyRequiresAdmin);
  }, [query, method]);

  // (B) Recompute “missing scopes” each time the current token or neededScopes changes
  useEffect(() => {
    if (!currentAccessToken) {
      setMissingScopes(neededScopes);
      return;
    }
    if (neededScopes.length === 0) {
      setMissingScopes([]);
      return;
    }

    const decoded = decodeJwt(currentAccessToken);
    if (!decoded?.scp) {
      // If no “scp” claim at all => everything is missing
      setMissingScopes(neededScopes);
      return;
    }

    const tokenScopes = decoded.scp.split(" ");
    const notGranted = neededScopes.filter((s) => !tokenScopes.includes(s));
    setMissingScopes(notGranted);
  }, [currentAccessToken, neededScopes]);

  // ========================
  // 2) Tabs & UI events
  // ========================
  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
  };

  // The main “Run Query” logic, now uses `currentAccessToken`
  const handleRunQuery = async () => {
    setResponse("");
    setErrorMessage("");

  const url = `https://graph.microsoft.com/${graphVersion}${normalizeQuery(query)}`;

    let parsedHeaders: Record<string, string> = {};
    if (customHeaders.trim()) {
      try {
        parsedHeaders = JSON.parse(customHeaders);
      } catch (err) {
        setErrorMessage("Invalid JSON in custom headers.");
        return;
      }
    }

    const headers: Record<string, string> = {
      Authorization: `Bearer ${currentAccessToken}`,
      "Content-Type": "application/json",
      ...parsedHeaders
    };

    const fetchOptions: RequestInit = {
      method,
      headers
    };

    if (method !== "GET" && method !== "DELETE" && body) {
      try {
        const parsedBody = JSON.parse(body);
        fetchOptions.body = JSON.stringify(parsedBody);
      } catch (err: any) {
        console.error("Invalid JSON body:", err);
        setErrorMessage("Invalid JSON in request body.");
        return;
      }
    }

    try {
      const res = await fetch(url, fetchOptions);
      const statusLine = `Status: ${res.status} ${res.statusText}\n`;
      let responseText = await res.text();

      try {
        const data = JSON.parse(responseText);
        responseText = JSON.stringify(data, null, 2);
      } catch {
        // keep as raw if not valid JSON
      }

      const combinedResponse = `${statusLine}\n${responseText}`;
      setResponse(combinedResponse);      // if 401/403 => we suspect missing perms
      if (!res.ok && (res.status === 403 || res.status === 401)) {
        let errorBody;
        try {
          // Try to parse the error response
          errorBody = JSON.parse(responseText);
        } catch (e) {
          // Not JSON or invalid format
          errorBody = null;
        }

        // Check for specific error codes in the response
        const errorCode = errorBody?.error?.code || '';
        const errorMessage = errorBody?.error?.message || '';
        
        if (errorCode === 'Authorization_RequestDenied' || errorMessage.includes('permission')) {
          // Permission-specific error message
          setErrorMessage(
            `Permission error (HTTP ${res.status}). The required permissions might not be detected correctly. ` + 
            `Check the Permissions tab and click "Grant Missing Permissions" to request admin consent if needed.`
          );
        } else {
          // Generic 401/403 error
          setErrorMessage(
            `Authentication/authorization error (HTTP ${res.status}). You may need additional scopes or permissions. ` +
            `Check the Permissions tab and try the "Grant Missing Permissions" button.`
          );
        }
      }
    } catch (error: any) {
      console.error("[GraphExplorer] Error in handleRunQuery:", error);
      setResponse(`Request failed. See console for details.\n\n${error.message}`);
    }
  };

  /**
   * Request consent for any missing scopes using MSAL's popup flow.
   * If the user is not an admin, admin-only scopes won't succeed unless
   * they've previously been granted tenant-wide by an admin.
   */
  async function requestConsentForMissingScopes() {
    if (!missingScopes.length) {
      alert("No missing scopes to request consent for!");
      return;
    }
  
    // 1) If MSAL is still in the middle of something, bail out
    if (inProgress !== "none") {
      alert("Another interactive flow is in progress. Please wait for it to complete.");
      return;
    }
  
    // 2) If we've already started the popup, don't start again
    if (isRequestingConsent) {
      alert("Consent is already in progress. Please wait.");
      return;
    }
  
    setIsRequestingConsent(true);
  
    // 3) If you need admin consent, we rely on extraQueryParameters
    const request = {
      scopes: missingScopes,
      account: accounts[0],
      prompt: "consent", // valid MSAL prompt
      extraQueryParameters: requiresAdmin
        ? { prompt: "admin_consent" }
        : undefined,
    };
  
    try {
      const response = await instance.acquireTokenPopup(request);
      if (response?.accessToken) {
        setCurrentAccessToken(response.accessToken);
        alert("Consent successful! The new token includes additional scopes. You can Run Query again.");
      }
    } catch (err: any) {
      console.error("Consent request failed:", err);
  
      if (err.errorCode === "interaction_in_progress") {
        alert("Another popup is already open. Please close it first, or wait.");
      } else if (err instanceof InteractionRequiredAuthError) {
        alert("User canceled or additional steps are needed. Check console for details.");
      } else {
        alert(`Consent request failed: ${err.message || err}`);
      }
    } finally {
      setIsRequestingConsent(false);
    }
  }
  

  // ========================
  // 3) Render
  // ========================
  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 6 }}>
      {/* Header Card */}
      <Card
        variant="outlined"
        sx={{
          mb: 4,
          borderRadius: 2,
          overflow: 'hidden'
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            p: 2,
            backgroundColor: '#f4f8fb',
            borderBottom: '1px solid rgba(0, 0, 0, 0.12)'
          }}
        >
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={onBack}
            sx={{
              mr: 2,
              borderRadius: 2,
              pl: 1.5,
              color: "text.secondary",
              borderColor: "divider",
              "&:hover": {
                backgroundColor: "rgba(0,0,0,0.04)",
                borderColor: "primary.main",
                color: "primary.main"
              }
            }}
          >
            Back
          </Button>

          <Box
            sx={{
              borderLeft: '4px solid',
              borderColor: 'primary.main',
              pl: 2,
              py: 0.5,
            }}
          >
            <Typography
              variant="h4"
              sx={{
                fontWeight: 600,
                color: 'primary.main',
                fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
                letterSpacing: '-0.01em',
              }}
            >
              Graph Explorer
            </Typography>

            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
              }}
            >
              Test Microsoft Graph API endpoints with your current access token
            </Typography>
          </Box>
        </Box>
      </Card>

      {/* Main Content Grid */}
      <Grid container spacing={3}>
        {/* Left Column - Request Configuration */}
        <Grid item xs={12}>
        <Card variant="outlined" sx={{ borderRadius: 2, mb: 1 }}>
          <CardContent sx={{ p: 0 }}>
            <Typography
              variant="h6"
              sx={{
                px: 3,
                py: 2,
                borderBottom: '1px solid rgba(0,0,0,0.12)',
                backgroundColor: '#f9f9f9',
                fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <HttpIcon sx={{ mr: 1, color: 'primary.main' }} />
              Request Builder
            </Typography>

              <Box sx={{ p: 3 }}>
                {/* API Templates Component */}
                <GraphApiTemplatesComponent onApplyTemplate={handleApplyTemplate} />
                
                <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: 'wrap' }}>
                  <Autocomplete
                    freeSolo
                    options={COMMON_ENDPOINTS}
                    groupBy={(option) => option.category}
                    getOptionLabel={(option) => typeof option === 'string' ? option : option.value}
                    renderOption={(props, option) => (
                      <li {...props}>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'monospace' }}>
                            {option.value}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {option.description}
                          </Typography>
                        </Box>
                      </li>
                    )}
                    value={query}
                    onChange={(event, newValue) => {
                      const val = typeof newValue === 'string' ? newValue : (newValue?.value || '');
                      setQuery(val);
                      setResponse("");
                      setErrorMessage("");
                    }}
                    onInputChange={(event, newInputValue) => {
                      setQuery(newInputValue);
                      setResponse("");
                      setErrorMessage("");
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Graph API Endpoint"
                        placeholder="Example: /me or /users"
                        fullWidth
                        variant="outlined"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            backgroundColor: '#ffffff',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                          },
                          '& .MuiInputLabel-root': {
                            fontWeight: 600,
                            color: 'primary.main'
                          },
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#c7d8f2',
                            borderWidth: 1.5
                          }
                        }}
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <>
                              <Box
                                component="span"
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.5,
                                  backgroundColor: '#e6f0ff',
                                  color: 'primary.main',
                                  fontFamily: 'monospace',
                                  fontWeight: 600,
                                  fontSize: '0.75rem',
                                  px: 1.25,
                                  py: 0.5,
                                  mr: 1,
                                  borderRadius: 1,
                                  border: '1px solid #c7d8f2'
                                }}
                              >
                                {graphVersion}
                              </Box>
                              {params.InputProps.startAdornment}
                            </>
                          ),
                          endAdornment: params.InputProps.endAdornment
                        }}
                      />
                    )}
                    sx={{ flex: 1 }}
                  />

                  <FormControl sx={{ 
                    minWidth: 100,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      backgroundColor: '#ffffff',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                    },
                    '& .MuiInputLabel-root': {
                      fontWeight: 600,
                      color: 'primary.main'
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#c7d8f2',
                      borderWidth: 1.5
                    }
                  }}>
                    <InputLabel id="http-method-label">Method</InputLabel>
                    <Select
                      labelId="http-method-label"
                      value={method}
                      label="Method"
                      onChange={(e) => {
                        setMethod(e.target.value as HttpMethod);
                        setResponse("");
                        setErrorMessage("");
                      }}
                      sx={{
                        borderRadius: 2,
                        fontWeight: 500
                      }}
                    >
                      <MenuItem value="GET">GET</MenuItem>
                      <MenuItem value="POST">POST</MenuItem>
                      <MenuItem value="PATCH">PATCH</MenuItem>
                      <MenuItem value="DELETE">DELETE</MenuItem>
                    </Select>
                  </FormControl>

                  <FormControl sx={{ minWidth: 95 }}>
                    <InputLabel id="graph-version-label">Version</InputLabel>
                    <Select
                      labelId="graph-version-label"
                      value={graphVersion}
                      label="Version"
                      onChange={(e) => {
                        const val = e.target.value as 'v1.0' | 'beta';
                        setGraphVersion(val);
                        setResponse('');
                        setQuery(prev => normalizeQuery(prev));
                      }}
                      sx={{ borderRadius: 2, fontWeight: 500 }}
                    >
                      <MenuItem value="v1.0">v1.0</MenuItem>
                      <MenuItem value="beta">beta</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                {graphVersion === 'beta' && (
                  <Alert severity="warning" icon={<InfoOutlinedIcon />} sx={{ mb: 2, borderRadius: 2 }}>
                    Beta version selected. Microsoft Graph beta APIs may change without notice and are not recommended for production scenarios.
                  </Alert>
                )}

                <Box sx={{ mb: 3 }}>
                  <Tabs
                    value={tabIndex}
                    onChange={handleTabChange}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{
                      mb: 2,
                      borderBottom: 1,
                      borderColor: 'divider',
                      '& .MuiTab-root': {
                        minWidth: 'auto',
                        fontWeight: 500,
                        fontSize: '0.875rem',
                        px: 2,
                      }
                    }}
                  >
                    <Tab label="Request Body" icon={<CodeIcon />} iconPosition="start" disabled={method === "GET" || method === "DELETE"} />
                    <Tab label="Headers" icon={<HttpIcon />} iconPosition="start" />
                    <Tab label="Permissions" icon={<SecurityIcon />} iconPosition="start" />
                    <Tab label="Access Token" icon={<VpnKeyIcon />} iconPosition="start" />
                  </Tabs>

                  {/* 1) Body Tab */}
                  {tabIndex === 0 && (
                    <Box>
                      {method === "GET" || method === "DELETE" ? (
                        <Alert severity="info" sx={{ borderRadius: 2 }}>
                          Request Body is disabled for GET/DELETE methods.
                        </Alert>
                      ) : (
                        <TextField
                          placeholder='{"key": "value"}'
                          multiline
                          minRows={8}
                          value={body}
                          onChange={(e) => setBody(e.target.value)}
                          fullWidth
                          variant="outlined"
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              fontFamily: 'monospace',
                              fontSize: '0.875rem',
                              borderRadius: 2,
                            }
                          }}
                        />
                      )}
                    </Box>
                  )}

                  {/* 2) Headers Tab */}
                  {tabIndex === 1 && (
                    <Box>
                      <TextField
                        placeholder='{"ConsistencyLevel": "eventual", "Prefer": "return=representation"}'
                        multiline
                        minRows={8}
                        value={customHeaders}
                        onChange={(e) => setCustomHeaders(e.target.value)}
                        fullWidth
                        variant="outlined"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            fontFamily: 'monospace',
                            fontSize: '0.875rem',
                            borderRadius: 2,
                          }
                        }}
                      />
                      
                      <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
                        Headers should be in valid JSON format with quotes around keys and values.
                        Example: {"{\"ConsistencyLevel\": \"eventual\"}"}
                      </Typography>
                      
                      {/* Add quick buttons for common headers */}
                      <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        <Button 
                          variant="outlined" 
                          size="small"
                          sx={{ borderRadius: 2 }}
                          onClick={() => {
                            try {
                              // Parse existing headers or create empty object
                              const existingHeaders = customHeaders.trim() ? 
                                JSON.parse(customHeaders) : {};
                              
                              // Add ConsistencyLevel header
                              const updatedHeaders = {
                                ...existingHeaders,
                                "ConsistencyLevel": "eventual"
                              };
                              
                              // Update headers field with formatted JSON
                              setCustomHeaders(JSON.stringify(updatedHeaders, null, 2));
                            } catch (err) {
                              // If current headers aren't valid JSON, just set new ones
                              setCustomHeaders(JSON.stringify({
                                "ConsistencyLevel": "eventual"
                              }, null, 2));
                            }
                          }}
                        >
                          + Add ConsistencyLevel: eventual
                        </Button>
                        
                        <Button 
                          variant="outlined" 
                          size="small"
                          sx={{ borderRadius: 2 }}
                          onClick={() => {
                            try {
                              const existingHeaders = customHeaders.trim() ? 
                                JSON.parse(customHeaders) : {};
                              
                              const updatedHeaders = {
                                ...existingHeaders,
                                "Prefer": "return=representation"
                              };
                              
                              setCustomHeaders(JSON.stringify(updatedHeaders, null, 2));
                            } catch (err) {
                              setCustomHeaders(JSON.stringify({
                                "Prefer": "return=representation"
                              }, null, 2));
                            }
                          }}
                        >
                          + Add Prefer: return=representation
                        </Button>
                      </Box>
                    </Box>
                  )}

                  {/* 3) Permissions Tab */}
                  {tabIndex === 2 && (
                    <Box sx={{ p: 2, backgroundColor: "#f9f9f9", borderRadius: 2 }}>
                      <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                        Required Permissions
                      </Typography>

                      {neededScopes.length === 0 ? (
                        <Alert severity="info" sx={{ borderRadius: 2 }}>
                          No specific scopes detected for this endpoint.
                        </Alert>
                      ) : (
                        <Box>
                          <Typography variant="body2" sx={{ mb: 1.5 }}>
                            <strong>Detected scopes:</strong>
                          </Typography>

                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                            {neededScopes.map(scope => (
                              <Chip
                                key={scope}
                                label={scope}
                                color="primary"
                                variant="outlined"
                                size="small"
                              />
                            ))}
                          </Box>

                          {missingScopes.length > 0 ? (
                            <>
                              <Divider sx={{ my: 2 }} />
                              <Typography variant="body2" sx={{ mb: 1.5, color: 'warning.dark', fontWeight: 500 }}>
                                <strong>Missing scopes:</strong>
                              </Typography>

                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                                {missingScopes.map(scope => (
                                  <Chip
                                    key={scope}
                                    label={scope}
                                    color="warning"
                                    variant="outlined"
                                    size="small"
                                  />
                                ))}
                              </Box>

                              {requiresAdmin && (
                                <Alert severity="warning" sx={{ mt: 2, mb: 2, borderRadius: 2 }}>
                                  Admin privileges required for these scopes
                                </Alert>
                              )}

                              <Button
                                variant="contained"
                                color="primary"
                                sx={{ mt: 1, borderRadius: 2 }}
                                disabled={isRequestingConsent}
                                onClick={requestConsentForMissingScopes}
                                startIcon={isRequestingConsent ? <CircularProgress size={16} /> : <SecurityIcon />}
                              >
                                {isRequestingConsent ? "Requesting..." : "Grant Missing Permissions"}
                              </Button>
                            </>
                          ) : (
                            <Alert severity="success" sx={{ mt: 2, borderRadius: 2 }}>
                              All required scopes are already granted!
                            </Alert>
                          )}
                        </Box>
                      )}
                    </Box>
                  )}

                  {/* 4) Access Token Tab */}
                  {tabIndex === 3 && (
                    <Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          Current Access Token
                        </Typography>

                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={copySuccess ? <DoneIcon /> : <ContentCopyIcon />}
                          onClick={() => handleCopyToClipboard(currentAccessToken)}
                          sx={{
                            borderRadius: 2,
                            color: copySuccess ? 'success.main' : 'primary.main',
                            borderColor: copySuccess ? 'success.main' : 'primary.main',
                          }}
                        >
                          {copySuccess ? "Copied!" : "Copy Token"}
                        </Button>
                      </Box>

                      <Paper
                        elevation={0}
                        variant="outlined"
                        sx={{
                          backgroundColor: "#f4f4f4",
                          p: 2,
                          borderRadius: 2,
                          maxHeight: 150,
                          overflowY: "auto",
                          overflowWrap: "break-word",
                          wordBreak: "break-all"
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            whiteSpace: "pre-wrap",
                            fontFamily: "monospace",
                            fontSize: '0.75rem'
                          }}
                        >
                          {currentAccessToken}
                        </Typography>
                      </Paper>

                      <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          Token Info
                        </Typography>

                        {currentAccessToken && (
                          <Box sx={{ mt: 1 }}>
                            <Grid container spacing={1}>
                              <Grid item xs={4}>
                                <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                  Issued to:
                                </Typography>
                              </Grid>
                              <Grid item xs={8}>
                                <Typography variant="caption">
                                  {decodeJwt(currentAccessToken)?.name || decodeJwt(currentAccessToken)?.upn || "Unknown"}
                                </Typography>
                              </Grid>

                              <Grid item xs={4}>
                                <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                  Expires:
                                </Typography>
                              </Grid>
                              <Grid item xs={8}>
                                <Typography variant="caption">
                                  {decodeJwt(currentAccessToken)?.exp
                                    ? new Date(decodeJwt(currentAccessToken).exp * 1000).toLocaleString()
                                    : "Unknown"}
                                </Typography>
                              </Grid>

                              <Grid item xs={4}>
                                <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                  Scopes:
                                </Typography>
                              </Grid>
                              <Grid item xs={8}>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    display: '-webkit-box',
                                    WebkitBoxOrient: 'vertical',
                                    WebkitLineClamp: 2,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis'
                                  }}
                                >
                                  {decodeJwt(currentAccessToken)?.scp || "None"}
                                </Typography>
                              </Grid>
                            </Grid>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  )}
                </Box>

                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  {errorMessage && (
                    <Alert
                      severity="error"
                      sx={{
                        borderRadius: 2,
                        flex: 1,
                        mr: 2
                      }}
                    >
                      {errorMessage}
                    </Alert>
                  )}

                  <Button
                    variant="contained"
                    onClick={handleRunQuery}
                    startIcon={<SendIcon />}
                    sx={{
                      borderRadius: 2,
                      px: 3,
                      boxShadow: '0 2px 4px rgba(25, 118, 210, 0.2)',
                      backgroundColor: '#1976d2',
                      '&:hover': {
                        backgroundColor: '#1565c0',
                        boxShadow: '0 4px 8px rgba(25, 118, 210, 0.3)'
                      }
                    }}
                  >
                    Run Query
                  </Button>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Right Column - Response Viewer */}
        <Grid item xs={12}>
        <Card variant="outlined" sx={{ borderRadius: 2, mb: 3 }}>
          <CardContent sx={{ p: 0 }}>
            <Typography
              variant="h6"
              sx={{
                px: 3,
                py: 2,
                borderBottom: '1px solid rgba(0,0,0,0.12)',
                backgroundColor: '#f9f9f9',
                fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <CodeIcon sx={{ mr: 1, color: 'primary.main' }} />
                Response
              </Box>
              
              {response && (
                <Tooltip title="Copy response">
                  <IconButton 
                    size="small" 
                    onClick={() => handleCopyToClipboard(response)}
                    sx={{ color: copySuccess ? 'success.main' : 'action.active' }}
                  >
                    {copySuccess ? <DoneIcon /> : <ContentCopyIcon />}
                  </IconButton>
                </Tooltip>
              )}
            </Typography>
            
            <Box sx={{ p: 2 }}>
              {!response ? (
                <Box 
                  sx={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: 300, // Maintain reasonable height
                    backgroundColor: '#f9f9f9',
                    borderRadius: 2,
                    p: 4,
                    border: '1px dashed rgba(0,0,0,0.12)'
                  }}
                >
                  <HttpIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                  <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
                    No response yet
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
                    Click the "Run Query" button to execute your request and see results here
                  </Typography>
                </Box>
              ) : (
                <Paper
                  elevation={0}
                  variant="outlined"
                  sx={{
                    backgroundColor: "#f4f4f4",
                    p: 3,
                    borderRadius: 2,
                    height: 400, // Increased height for better viewing
                    maxHeight: 'none', // Remove max height constraint
                    overflowY: "auto"
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ 
                      whiteSpace: "pre-wrap", 
                      fontFamily: "monospace",
                      fontSize: '0.875rem'
                    }}
                  >
                    {response}
                  </Typography>
                </Paper>
              )}
            </Box>
          </CardContent>
        </Card>
      </Grid>
    </Grid>

      {/* About Graph Explorer - Expandable Accordion */}
      <Card variant="outlined" sx={{ borderRadius: 2, mt: 3 }}>
        <Accordion expanded={aboutExpanded} onChange={() => setAboutExpanded(!aboutExpanded)}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <InfoOutlinedIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                About Microsoft Graph Explorer
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={3}>
              <Typography variant="body2" color="text.secondary">
                This tool enables you to explore and test Microsoft Graph API endpoints directly from your browser. 
                Microsoft Graph is the unified API endpoint for accessing data and intelligence across Microsoft 365, 
                Windows, and Enterprise Mobility + Security services.
              </Typography>

              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
                  Key Features:
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <Paper variant="outlined" sx={{ p: 2, bgcolor: 'rgba(232, 245, 233, 0.3)', borderColor: '#4caf50', height: '100%' }}>
                      <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600, color: '#2e7d32', display: 'flex', alignItems: 'center' }}>
                        <HttpIcon sx={{ mr: 0.5, fontSize: '1.2rem' }} />
                        Endpoint Autocomplete
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Start typing "/" in the endpoint field to see suggestions for common Graph API endpoints. 
                        Endpoints are organized by category (Users, Groups, Applications, etc.) with descriptions 
                        to help you find the right API call.
                      </Typography>
                    </Paper>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Paper variant="outlined" sx={{ p: 2, bgcolor: 'rgba(227, 242, 253, 0.3)', borderColor: '#2196f3', height: '100%' }}>
                      <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600, color: '#1976d2', display: 'flex', alignItems: 'center' }}>
                        <SecurityIcon sx={{ mr: 0.5, fontSize: '1.2rem' }} />
                        Smart Permission Detection
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        The tool automatically detects required OAuth scopes based on your endpoint and HTTP method. 
                        Check the Permissions tab to see what's needed and request consent for missing scopes.
                      </Typography>
                    </Paper>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Paper variant="outlined" sx={{ p: 2, bgcolor: 'rgba(255, 243, 224, 0.3)', borderColor: '#ff9800', height: '100%' }}>
                      <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600, color: '#e65100', display: 'flex', alignItems: 'center' }}>
                        <CodeIcon sx={{ mr: 0.5, fontSize: '1.2rem' }} />
                        API Templates
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Use pre-built templates for common operations like querying users, creating groups, 
                        or managing applications. Templates include proper headers and request bodies.
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </Box>

              <Divider />

              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
                  Common Use Cases:
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ p: 2, bgcolor: '#f9f9f9', borderRadius: 1, borderLeft: '4px solid #2e7d32' }}>
                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                        User Profile & Calendar
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        • <code>/me</code> - Get your profile information
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        • <code>/me/messages</code> - Access your emails
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        • <code>/me/events</code> - Manage calendar events
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        • <code>/me/drive</code> - Access your OneDrive files
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Box sx={{ p: 2, bgcolor: '#f9f9f9', borderRadius: 1, borderLeft: '4px solid #1976d2' }}>
                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                        Directory & Administration
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        • <code>/users</code> - List and manage users (requires admin)
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        • <code>/groups</code> - Manage Microsoft 365 groups
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        • <code>/applications</code> - Manage app registrations
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        • <code>/auditLogs/directoryAudits</code> - Access audit logs
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Box sx={{ p: 2, bgcolor: '#f9f9f9', borderRadius: 1, borderLeft: '4px solid #e65100' }}>
                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                        Security & Compliance
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        • <code>/identity/conditionalAccess/policies</code> - Conditional access
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        • <code>/identityProtection/riskyUsers</code> - Identity protection
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        • <code>/security/alerts</code> - Security alerts
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        • <code>/auditLogs/signIns</code> - Sign-in activity logs
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Box sx={{ p: 2, bgcolor: '#f9f9f9', borderRadius: 1, borderLeft: '4px solid #7b1fa2' }}>
                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                        Collaboration
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        • <code>/me/chats</code> - Access Teams chats
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        • <code>/teams/{"{id}"}/channels</code> - Manage Teams channels
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        • <code>/sites/root</code> - Access SharePoint sites
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        • <code>/planner/plans</code> - Manage Planner tasks
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>

              <Divider />

              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
                  Query Tips & Best Practices:
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                      <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: 'primary.main' }}>
                        OData Query Parameters
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        <strong>$select</strong> - Limit returned properties:<br />
                        <code style={{ fontSize: '0.75rem', backgroundColor: '#f4f4f4', padding: '2px 4px', borderRadius: '3px' }}>
                          /users?$select=displayName,mail
                        </code>
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        <strong>$filter</strong> - Filter results:<br />
                        <code style={{ fontSize: '0.75rem', backgroundColor: '#f4f4f4', padding: '2px 4px', borderRadius: '3px' }}>
                          /users?$filter=startswith(displayName,'A')
                        </code>
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        <strong>$top</strong> - Limit number of results:<br />
                        <code style={{ fontSize: '0.75rem', backgroundColor: '#f4f4f4', padding: '2px 4px', borderRadius: '3px' }}>
                          /users?$top=10
                        </code>
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        <strong>$count</strong> - Get total count (requires ConsistencyLevel header):<br />
                        <code style={{ fontSize: '0.75rem', backgroundColor: '#f4f4f4', padding: '2px 4px', borderRadius: '3px' }}>
                          /users?$count=true
                        </code>
                      </Typography>
                    </Paper>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                      <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: 'primary.main' }}>
                        Important Headers
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        <strong>ConsistencyLevel: eventual</strong><br />
                        Required for $count and advanced queries with $filter
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        <strong>Prefer: return=representation</strong><br />
                        Returns the updated object after POST/PATCH operations
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Content-Type: application/json</strong><br />
                        Automatically added for requests with body data
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </Box>

              <Alert severity="info" sx={{ borderRadius: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Version Selection: v1.0 vs beta
                </Typography>
                <Typography variant="body2">
                  • <strong>v1.0</strong>: Production-ready, stable APIs with guaranteed support and SLAs<br />
                  • <strong>beta</strong>: Preview APIs with latest features but subject to breaking changes
                </Typography>
              </Alert>

              <Box sx={{ p: 2, bgcolor: 'rgba(25, 118, 210, 0.05)', borderRadius: 2, border: '1px solid rgba(25, 118, 210, 0.2)' }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                  📚 Learn More:
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  • <a href="https://learn.microsoft.com/en-us/graph/overview" target="_blank" rel="noopener noreferrer" 
                    style={{ color: '#1976d2', textDecoration: 'none' }}>Microsoft Graph Overview</a><br />
                  • <a href="https://learn.microsoft.com/en-us/graph/api/overview" target="_blank" rel="noopener noreferrer"
                    style={{ color: '#1976d2', textDecoration: 'none' }}>API Reference Documentation</a><br />
                  • <a href="https://learn.microsoft.com/en-us/graph/permissions-reference" target="_blank" rel="noopener noreferrer"
                    style={{ color: '#1976d2', textDecoration: 'none' }}>Permissions Reference</a><br />
                  • <a href="https://learn.microsoft.com/en-us/graph/query-parameters" target="_blank" rel="noopener noreferrer"
                    style={{ color: '#1976d2', textDecoration: 'none' }}>Query Parameter Reference</a>
                </Typography>
              </Box>
            </Stack>
          </AccordionDetails>
        </Accordion>
      </Card>
    </Container>
  );
}

export default GraphExplorer;
